import React, {Component} from "react";
import {Moment} from "moment";
// @ts-ignore
import DateTimePicker from "react-datetime-picker";
import {FirebaseService} from "../../../services/firebase/FirebaseService";
import {DeliveryStatus} from "../../../constants/DeliveryStatus";
import {DeliveryProgress} from "../../components/DeliveryProgressBar";
import {RequestPayload} from "../types/RequestPayload";
import {DeliveryAreas} from "../constants/DeliveryAreas";
import {CreateAtDatabaseService} from "../../../services/database/CreateAtDatabaseService";

const moment = require("moment");

/**
 * Local interface to define the types of the state
 */
interface IState {
  weight?: number;
  height?: number;
  width?: number;
  depth?: number;
  trackingID?: string;
  receiverEmail?: string;

  from?: string;
  to?: string;
  senderName?: string;

  category?: "1" | "2" | "3" | "4";

  estimatedTimeOfArrival: Moment;
  deliveryTimePeriodStart: Moment;

  deliveryArea?: number;
}

/**
 * Cross Innovation Class 2021
 *
 * Team: Kühne + Nagel
 *
 * Kunden Web App
 *
 * This class is used to create new deliveries at the database and display the trackingID to the user.
 * In the background an email will be send via cloud functions to the user.
 *
 * @author Tobias Ramm <winf103614@fh-wedel,de>
 * @version last modification: 29.06.2021
 *
 */
export default class CreateNewDelivery extends Component<any> {
  state: Readonly<IState> = {
    estimatedTimeOfArrival: moment(),
    deliveryTimePeriodStart: moment(),
    category: "2",
  };

  /**
   * Will be called when the user presses on submit
   */
  async handleSubmit() {
    const {
      width,
      weight,
      height,
      depth,
      estimatedTimeOfArrival,
      from,
      to,
      senderName,
      deliveryTimePeriodStart,
      receiverEmail,
      deliveryArea,
    } = this.state;

    if (width && !!weight && height && depth) {
      let category = this.state.category;

      if (weight < 10) {
        category = "1";
      } else if (weight < 30) {
        category = "2";
      } else if (weight < 60) {
        category = "3";
      } else {
        category = "4";
      }

      try {
        const sendValues: RequestPayload = {
          weight: weight,
          height: height,
          width: width,
          depth: depth,
          from: from,
          to: to,
          senderName: senderName,
          receiverEmail: receiverEmail,
          estimatedTimeOfArrival: estimatedTimeOfArrival.toISOString(),
          status: DeliveryStatus.active,
          deliveryTimePeriodStart: deliveryTimePeriodStart.toISOString(),
          category: category,
          deliveryProgress: DeliveryProgress.CAPTURE,
          deliveryArea: deliveryArea as number,
        };

        // create the new delivery
        const trackingID: string = await CreateAtDatabaseService.createNewDelivery(
          sendValues
        );

        // call cloud functions to send the email
        const handleNewDelivery = FirebaseService.functions().httpsCallable(
          "handleNewDelivery"
        );

        handleNewDelivery({
          ...sendValues,
          trackingID: trackingID,
        }).catch((e) =>
          console.error("Error: Returned from cloud function", e)
        );

        this.setState({
          trackingID: trackingID,
        });
      } catch (e) {
        alert(e);
      }
    }
  }

  /**
   * Renders a form with the possible delivery areas
   */
  renderDeliveryAreas(): JSX.Element {
    const values: JSX.Element[] = [];

    for (const [key, value] of Object.entries(DeliveryAreas)) {
      values.push(<option value={key}>{key + " - " + value}</option>);
    }
    return (
      <label>
        Zustellbereich:
        <select
          name="category"
          id="category"
          onChange={(evt) => {
            this.setState({ deliveryArea: evt.target.value });
          }}
          value={this.state.deliveryArea}
        >
          {values}
        </select>
      </label>
    );
  }

  render() {
    return (
      <div className={"createNewRouteContainer"}>
        {!!this.state.trackingID && (
          <div className={"successBox"}>
            <h1>Lieferung erfolgreich übertragen</h1>

            <h3>Tracking-ID: {this.state.trackingID}</h3>
          </div>
        )}

        <div className={"contentBox"}>
          <h1>Lieferung erstellen</h1>

          <div className={"column"}>
            <label>
              Gewicht (in kg):
              <input
                type="number"
                value={this.state.weight}
                placeholder={"z.B. 250"}
                onChange={(evt) => this.setState({ weight: evt.target.value })}
                className={"input"}
              />
            </label>
          </div>

          <div className={"row"}>
            <label>
              Von:
              <input
                type="string"
                value={this.state.from}
                placeholder={"z.B. Bremen"}
                onChange={(evt) => this.setState({ from: evt.target.value })}
                className={"input"}
              />
            </label>

            <label>
              Nach:
              <input
                type="string"
                value={this.state.to}
                placeholder={"z.B. Hamburg"}
                onChange={(evt) => this.setState({ to: evt.target.value })}
                className={"input"}
              />
            </label>
          </div>

          <div className={"column"}>
            <label>
              Sender:
              <input
                type="string"
                value={this.state.senderName}
                placeholder={"z.B. mediamarkt.de"}
                onChange={(evt) =>
                  this.setState({ senderName: evt.target.value })
                }
                className={"input"}
              />
            </label>
          </div>

          <div className={"row"}>
            <label>
              Breite (in m):
              <input
                type="number"
                value={this.state.width}
                placeholder={"z.B. 2.50"}
                onChange={(evt) => this.setState({ width: evt.target.value })}
                className={"input"}
              />
            </label>

            <label>
              Höhe (in m):
              <input
                type="number"
                value={this.state.height}
                placeholder={"z.B. 1.00"}
                onChange={(evt) => this.setState({ height: evt.target.value })}
                className={"input"}
              />
            </label>

            <label>
              Tiefe (in m):
              <input
                type="number"
                value={this.state.depth}
                placeholder={"z.B. 0.50"}
                onChange={(evt) => this.setState({ depth: evt.target.value })}
                className={"input"}
              />
            </label>
          </div>

          <div className={"column"}>
            <div>Estimated Time of Arrival:</div>

            <DateTimePicker
              onChange={(value: Date) =>
                this.setState({ estimatedTimeOfArrival: moment(value) })
              }
              value={this.state.estimatedTimeOfArrival.toDate()}
              clearIcon={null}
              calendarAriaLabel={"Estimated Time of Arrival:"}
              className={"input"}
            />
          </div>

          <div className={"column"}>
            <div>
              Zustellzeitraum von (bis sind automatisch 2h in der Zukunft):
            </div>

            <DateTimePicker
              onChange={(value: Date) =>
                this.setState({ deliveryTimePeriodStart: moment(value) })
              }
              value={this.state.deliveryTimePeriodStart.toDate()}
              clearIcon={null}
              calendarAriaLabel={"Estimated Time of Arrival:"}
              className={"input"}
              disableCalendar={true}
            />
          </div>
          <div className={"column"}>
            <label>
              Email:
              <input
                type="string"
                value={this.state.receiverEmail}
                placeholder={"foo@example.com"}
                onChange={(evt) =>
                  this.setState({ receiverEmail: evt.target.value })
                }
                className={"input"}
              />
            </label>
          </div>

          <div className={"column"}>{this.renderDeliveryAreas()}</div>

          <div>
            <button onClick={() => this.handleSubmit()}>Absenden</button>
          </div>
        </div>
      </div>
    );
  }
}
